import { c as _c } from "react/compiler-runtime";
import { PaymentMethods } from 'api-graphql-types';
import { type ReactNode, useEffect, useEffectEvent, useState } from 'react';
import NativePaymentMethodsContext from './NativePaymentMethodsContext';
import type { NativePaymentAvailable, NativePaymentMethod } from './types';
function getSupportedMethods(paymentMethod: NativePaymentMethod): [PaymentMethodData] {
  if (paymentMethod === PaymentMethods.StripeApplePay) {
    return [{
      supportedMethods: 'https://apple.com/apple-pay',
      data: {
        environment: 'TEST'
      }
    }];
  }
  return [{
    supportedMethods: 'https://google.com/pay',
    data: {
      currencyCode: 'EUR',
      countryCode: 'DE'
    }
  }];
}
async function isPaymentMethodAvailable(paymentMethod: NativePaymentMethod): Promise<boolean> {
  if (typeof PaymentRequest !== 'undefined') {
    try {
      return await new PaymentRequest(getSupportedMethods(paymentMethod), {
        total: {
          label: 'test',
          amount: {
            currency: 'EUR',
            value: '0.00'
          }
        }
      }).canMakePayment();
    } catch {}
  }
  try {
    // browser does not support PaymentRequest
    if (paymentMethod === PaymentMethods.StripeApplePay && typeof window.ApplePaySession !== 'undefined') {
      return (window.ApplePaySession as {
        canMakePayments: () => Promise<boolean>;
      }).canMakePayments() ?? false;
    }
    if (paymentMethod === PaymentMethods.StripeGooglePay && typeof window.google !== 'undefined') {
      // assumes google sdk is loaded
      return (window.google as {
        payments?: {
          isReadyToPay: () => Promise<boolean>;
        };
      }).payments?.isReadyToPay() ?? false;
    }
  } catch (e) {
    console.error(e);
  }
  return false;
}
const initialAvailabilityState = {
  [PaymentMethods.StripeApplePay]: false,
  [PaymentMethods.StripeGooglePay]: false
};
export function useCheckPaymentMethodAvailability() {
  const $ = _c(7);
  const [available, setAvailable] = useState(initialAvailabilityState);
  const [loading, setLoading] = useState(true);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async () => {
      setLoading(true);
      const results = await Promise.allSettled([isPaymentMethodAvailable(PaymentMethods.StripeApplePay), isPaymentMethodAvailable(PaymentMethods.StripeGooglePay)]);
      const resultKeys = [PaymentMethods.StripeApplePay, PaymentMethods.StripeGooglePay] as const;
      setAvailable(prevState => {
        const newState = {
          ...prevState
        };
        resultKeys.forEach((key, index) => {
          const result = results[index];
          const value = result.status === "fulfilled" ? result.value : false;
          newState[key] = value;
        });
        return newState;
      });
      setLoading(false);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const checkPaymentMethodAvailability = useEffectEvent(t0);
  let t1;
  if ($[1] !== checkPaymentMethodAvailability) {
    t1 = () => {
      checkPaymentMethodAvailability();
      return () => {
        setLoading(true);
        setAvailable(initialAvailabilityState);
      };
    };
    $[1] = checkPaymentMethodAvailability;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== available || $[5] !== loading) {
    t3 = {
      available,
      loading
    };
    $[4] = available;
    $[5] = loading;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function NativePaymentMethodsProvider(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  const {
    available,
    loading
  } = useCheckPaymentMethodAvailability();
  let t1;
  if ($[0] !== available || $[1] !== loading) {
    t1 = {
      available,
      loading
    };
    $[0] = available;
    $[1] = loading;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <NativePaymentMethodsContext.Provider value={t1}>{children}</NativePaymentMethodsContext.Provider>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export default NativePaymentMethodsProvider;